import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Dropdown, DropdownList } from '.';
import { toFilterString } from '../../../util/string.util';
import { Feedback } from '../Feedback/feedback';
import TextInput from '../TextInput/TextInput';
import { v4 } from 'uuid';
import useControllableState, { StateManager } from '../../../hooks/useControllableState';

interface DropdownSearchSelectBaseProps {
  label?: string;
  placeholder: string;

  items: string[];
  textBuilder: (item: string) => string;
  itemBuilder?: (item: string) => React.ReactNode;
  searchBuilder?: (item: string) => string;

  mandatory?: boolean;
  disabled?: boolean;
  feedback?: (item: string | undefined) => Feedback.Props | undefined;

  first?: React.ReactNode;
  lastBuilder?: (options: { input: string; selected: boolean; closeDropdown?: () => void }) => React.ReactNode;

  leading?: React.ReactNode;
  tooltip?: string | React.ReactNode;

  openManager?: StateManager<boolean>;
}

interface DropdownTextSelectProps extends DropdownSearchSelectBaseProps {
  /** The currently selected value */
  value: string | undefined;
  onSelect: (item: string | undefined) => void;
}

export default function DropdownTextSelect({
  value,
  onSelect,
  openManager,
  label,
  placeholder,
  items,
  textBuilder,
  itemBuilder,
  searchBuilder,
  lastBuilder,
  mandatory = false,
  disabled = false,
  feedback = mandatory ? item => Feedback.mandatory(item) : item => undefined,
  leading,
  tooltip,
}: DropdownTextSelectProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const [opened, setOpened] = useState(true);
  const [feedbackProps, setFeedbackProps] = useState<Feedback.Props | undefined>(undefined);
  const [open, setOpen] = useControllableState(false, openManager);

  const [search, setSearch] = useState('');

  useEffect(() => {
    if (open && !opened) setOpened(true);
  }, [open]);

  useEffect(() => {
    if (!opened) setFeedbackProps(feedback(value));
  }, [value, mandatory]);

  const handleSelect = (item: string | undefined) => {
    onSelect(item);
    setOpen(false);
  };

  const shownItems = useMemo(() => {
    return items.filter(i =>
      toFilterString(searchBuilder ? searchBuilder(i) : textBuilder(i)).includes(toFilterString(search)),
    );
  }, [items, search]);

  const [externalUpdate, setExternalUpdate] = useState(v4());
  useEffect(() => {
    setExternalUpdate(v4());
  }, [feedbackProps]);

  return (
    <div className='grid gap-1'>
      <Dropdown
        externalUpdate={externalUpdate}
        open={open}
        setOpen={setOpen}
        dropdown={
          <DropdownList
            items={shownItems}
            textBuilder={textBuilder}
            itemBuilder={itemBuilder}
            onClick={handleSelect}
            lastBuilder={
              lastBuilder
                ? selected => lastBuilder!({ input: search, selected, closeDropdown: () => setOpen(false) })
                : undefined
            }
          />
        }
      >
        <TextInput
          leading={leading}
          ref={inputRef}
          disabled={disabled}
          value={value ? textBuilder(value) : search}
          onChange={search => {
            setSearch(search);
            onSelect(search);
          }}
          label={label}
          mandatory={mandatory}
          feedback={null}
          placeholder={placeholder}
          tooltip={tooltip}
          onFocus={() => setOpen(true)}
        />
      </Dropdown>
      {feedbackProps?.text && !open && (
        <span className='flex gap-1'>
          {Feedback.icons[feedbackProps.type]}
          <p className={`text-xs ${Feedback.textColors[feedbackProps.type]}`}>{feedbackProps.text}</p>
        </span>
      )}
    </div>
  );
}
